<template>
  <div class="reply-option-container">
    <div class="reply-option-row">
      <div class="reply-option">
        <div class="reply-option-text">
          <TextEditor
            :value="option.text"
            :show-variable-picker="false"
            :placeholder="'Option'"
            @text-updated="textUpdated"
          />
        </div>
      </div>
      <div class="reply-option-icon reply-option-arrow">
        <n-tooltip
          position="top"
          text="Options will be presented to the visitor for them to select as their reply. When selected, the flow will transition to the corresponding path"
        >
          <font-awesome-icon icon="chevron-right"></font-awesome-icon>
        </n-tooltip>
      </div>
      <div class="reply-option-transition">
        <TransitionPicker :transition="option.transition" @transition-selected="transitionSelected" />
      </div>
      <div class="reply-option-icon reply-option-delete">
        <a class="delete is-small" @click="$emit('delete-option', index)"></a>
      </div>
    </div>

    <div class="reply-option-row reply-option-row-errors">
      <div class="reply-option">
        <p class="help is-danger" v-if="isTextRequiredInvalid">Must not be empty</p>
        <p class="help is-danger" v-if="isTextUniqueInvalid">Must be unique</p>
        <p class="help is-danger" v-if="isTextMatchesTypeInvalid">Must be a valid {{ dataTypeErrorMessage }}</p>
      </div>
      <div class="reply-option-transition">
        <p class="help is-danger" v-if="isTransitionRequiredInvalid">Must not be empty</p>
        <p class="help is-danger" v-if="isTransitionValidInvalid">Must transition to a live flow</p>
        <p class="help is-danger" v-if="isTransitionPayloadInvalid">Must not contain an invalid transition</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import TextEditor from '@/features/bots/components/designer/TextEditor/TextEditor.vue';
import TransitionPicker from './TransitionPicker.vue';
import { QuickReplyViewModel } from '@/features/bots/models/designer';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import VuelidateUtil from '@shared/util/vuelidateUtil';
import { useStore } from 'vuex';

const props = defineProps({
  option: {
    type: Object as PropType<QuickReplyViewModel>,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  allOptions: {
    type: Array as PropType<QuickReplyViewModel[]>,
    required: true,
  },
  pattern: {
    type: String,
    default: '',
  },
  dataTypeErrorMessage: {
    type: String,
    default: '',
  },
  hasDataType: {
    type: Boolean,
    default: false,
  },
  isValidTransition: {
    type: Function as PropType<(transition: TransitionViewModel) => boolean>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'text-updated', index: number, text: string): void;
  (e: 'transition-selected', index: number, transition: TransitionViewModel): void;
  (e: 'delete-option', index: number): void;
}>();

const store = useStore();

const validations = computed(() => ({
  option: {
    text: {
      required,
      isUnique(value: string) {
        return props.allOptions.filter((opt: QuickReplyViewModel) => opt.text === value).length <= 1;
      },
      matchesType(value: string) {
        return !props.pattern || !!value.match(props.pattern);
      },
    },
    transition: {
      type: {
        required,
      },
      isValidTransition(transition: TransitionViewModel) {
        return props.isValidTransition(transition);
      },
      payload: {
        payloadValid(payload: string) {
          return payload ? !store.getters.isTransitionPayloadInvalid(payload).value : true;
        },
      },
    },
  },
}));

const $v = useVuelidate(validations, { option: props.option });
const isTextRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.option.text));
const isTextUniqueInvalid = computed(() => VuelidateUtil.isValidationInvalid($v, (v) => v.option.text, 'isUnique'));
const isTextMatchesTypeInvalid = computed(
  () => props.hasDataType && VuelidateUtil.isValidationInvalid($v, (v) => v.option.text, 'matchesType'),
);
const isTransitionRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.option.transition));
const isTransitionValidInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.option.transition, 'isValidTransition'),
);
const isTransitionPayloadInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.option.transition, 'payloadValid'),
);

function textUpdated(text: string) {
  $v.value.option.text.$touch();
  emit('text-updated', props.index, text);
}

function transitionSelected(transition: TransitionViewModel) {
  $v.value.option.transition.$touch();
  emit('transition-selected', props.index, transition);
}
</script>

<style scoped lang="scss">
// Copy relevant styles from the original component
.reply-option-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;

  &:focus-within {
    z-index: 2;
  }
}

.reply-option-row {
  flex: 1;
  display: flex;
  align-items: center;
}

.reply-option-errors {
  flex: 1;
}

.reply-option {
  flex: 1;
}

.reply-option-text {
  border: solid 1px $color-netcall-deep-sea;
  border-radius: 10px;
  padding: 0.2em 0.45em;

  &:focus-within {
    box-shadow: 0 1px 8px 1px rgba(0, 0, 0, 0.2);
  }
  :deep(.editing) {
    border: none;
    box-shadow: none;
  }
}

.reply-option-icon {
  display: flex;
  padding-left: 0.5em;
  padding-right: 0.5em;
  padding-top: 0.25em;
  flex-shrink: 0;

  &.reply-option-arrow {
    color: $color-netcall-salmon;
  }
}

.reply-option-transition {
  flex: 1;
  position: relative;
  border-radius: 10px;
  justify-content: flex-end;
}

.delete {
  margin: 5px;
}
</style>
